/**
 * Um valor em reais escrito por extenso.
 *
 * Papel: aqui fica `valorPorExtenso`, o texto que o Recibo de Pagamento põe entre
 * parênteses depois do valor e que a Ordem de Pagamento escreve embaixo do
 * total.
 *
 * Como usar: a conta é a da origem — grupos de três dígitos, "cem" para cem
 * exato e "cento e…" para os demais, milhar, milhão e bilhão juntos por espaço,
 * e o "e" antes do último grupo quando ele é menor que cem ("mil e cinco") —,
 * com duas correções: o "e" também antes da centena exata ("mil e cem") e o "de"
 * depois de milhão ou bilhão exato ("um milhão de reais"). Fora delas, o texto
 * é o da origem, inclusive o "um milhão quinhentos mil reais" sem "e"; quem
 * mudar outra regra muda o que os recibos já emitidos diziam.
 */

const UNIDADES = ['', 'um', 'dois', 'três', 'quatro', 'cinco', 'seis', 'sete', 'oito', 'nove'];
const DE_DEZ_A_DEZENOVE = [
  'dez',
  'onze',
  'doze',
  'treze',
  'catorze',
  'quinze',
  'dezesseis',
  'dezessete',
  'dezoito',
  'dezenove',
];
const DEZENAS = [
  '',
  'dez',
  'vinte',
  'trinta',
  'quarenta',
  'cinquenta',
  'sessenta',
  'setenta',
  'oitenta',
  'noventa',
];
const CENTENAS = [
  '',
  'cento',
  'duzentos',
  'trezentos',
  'quatrocentos',
  'quinhentos',
  'seiscentos',
  'setecentos',
  'oitocentos',
  'novecentos',
];

/** Um grupo de 0 a 999 por extenso; zero é vazio. */
function grupo(numero: number): string {
  if (numero === 0) return '';
  if (numero === 100) return 'cem';
  const partes: string[] = [];
  const centena = Math.floor(numero / 100);
  const resto = numero % 100;
  if (centena > 0) partes.push(CENTENAS[centena] ?? '');
  if (resto > 0 && resto < 10) partes.push(UNIDADES[resto] ?? '');
  else if (resto >= 10 && resto < 20) partes.push(DE_DEZ_A_DEZENOVE[resto - 10] ?? '');
  else if (resto >= 20) {
    const dezena = DEZENAS[Math.floor(resto / 10)] ?? '';
    const unidade = resto % 10;
    partes.push(unidade > 0 ? `${dezena} e ${UNIDADES[unidade] ?? ''}` : dezena);
  }
  return partes.join(' e ');
}

/** Um inteiro não negativo por extenso, e se ele é milhão ou bilhão exato. */
function inteiro(numero: number): { texto: string; milhaoExato: boolean } {
  if (numero === 0) return { texto: 'zero', milhaoExato: false };
  const bilhoes = Math.floor(numero / 1e9);
  const milhoes = Math.floor((numero % 1e9) / 1e6);
  const milhares = Math.floor((numero % 1e6) / 1000);
  const resto = numero % 1000;
  const partes: string[] = [];
  if (bilhoes > 0) partes.push(bilhoes === 1 ? 'um bilhão' : `${grupo(bilhoes)} bilhões`);
  if (milhoes > 0) partes.push(milhoes === 1 ? 'um milhão' : `${grupo(milhoes)} milhões`);
  if (milhares > 0) partes.push(milhares === 1 ? 'mil' : `${grupo(milhares)} mil`);
  if (resto > 0) partes.push(grupo(resto));

  const milhaoExato = milhares === 0 && resto === 0 && (milhoes > 0 || bilhoes > 0);
  const ultima = partes.at(-1) ?? '';
  if (partes.length === 1) return { texto: ultima, milhaoExato };
  const cabeca = partes.slice(0, -1).join(' ');
  const comE = resto > 0 && (resto < 100 || resto % 100 === 0);
  return { texto: `${cabeca}${comE ? ' e ' : ' '}${ultima}`, milhaoExato };
}

/**
 * Escreve um valor em reais por extenso.
 *
 * Quem chama são o recibo e a ordem de pagamento, com o total do documento.
 *
 * @param valor - O valor em reais; arredondado a centavos antes da conta.
 * @returns "zero reais", "um real", "mil e cem reais", "um milhão de reais e
 *   cinquenta centavos".
 */
export function valorPorExtenso(valor: number): string {
  const arredondado = Math.round(valor * 100) / 100;
  const reais = Math.floor(arredondado + 1e-9);
  const centavos = Math.round((arredondado - reais) * 100);
  if (reais === 0 && centavos === 0) return 'zero reais';

  let texto = '';
  if (reais > 0) {
    const { texto: porExtenso, milhaoExato } = inteiro(reais);
    texto = `${porExtenso}${reais === 1 ? ' real' : milhaoExato ? ' de reais' : ' reais'}`;
  }
  if (centavos > 0) {
    if (reais > 0) texto += ' e ';
    texto += `${inteiro(centavos).texto}${centavos === 1 ? ' centavo' : ' centavos'}`;
  }
  return texto;
}
